/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import ICAL from 'ical.js';
import { DateTime } from 'luxon';

import IcalExpander from './expander/ical-expander';
import IcalSlicer from './slicer/ical-slicer';
import IcalTimeUtil from './ical-time-util';
import IcalUtil from './ical-util';
import config from '../../config';

// TODO Co přesně by mělo být obsahem této třídy?
// - Definice maxIterations na jednom místě?
// - Pohodlnější API než jen between: at, has, ...
// - Anebo zrušit a tyto operace přesunout do tříd, které IcalPlanner používají?
//   Asi počkat na skutečnou implementaci a porovnat opakování.
// TODO Hotfix nefunkčního plánování se změnami instancí opakování.
// - https://github.com/mifi/ical-expander/issues/14
// - Vyřešilo se přidáním "ical.js": "^1.4.0" do "resolutions" v "package.json".
// TODO Problémy s IcalExpander
// - Dovolit vstup v jcal formátu
// - Zveřejnění metody isEventWithinRange
/**
 * Plánovač iCalendar událostí
 *
 * Zdroje:
 * - [ical.js issue - expanding repeating event](https://github.com/mozilla-comm/ical.js/issues/285)
 * - [ical-expander](https://github.com/mifi/ical-expander)
 */
class IcalPlanner {
  /** Vytvoří instanci. */
  constructor() {
    // TODO IoC/DI
    // - Nyní jednoduše nelze, musel by se asi refaktorizovat celý schedule modul.
    // - Jako technický dluh, se tedy provede sestavení zde.
    //   V paměti jsou tak udržovány 2 instance těchto objektů.
    const util = new IcalUtil(ICAL);
    const timeUtil = new IcalTimeUtil(ICAL);
    const slicer = new IcalSlicer(ICAL, timeUtil, util);
    const expander = new IcalExpander(slicer, util);
    this.expander = expander;
    this.maxIterations = config.icalMaxIterations;
  }

  /**
   * Zjistí, jestli je v kalendáři nějaká aktivní událost pro datum na vstupu.
   * @param {string} ical Řetězec s iCalendar dle RFC 5545
   * @param {Date} date Datum pro které hledat událost
   * @returns {boolean}
   */
  hasEventOnDate(ical, date) {
    const calendar = ICAL.Component.fromString(ical);
    // Vyhledání událostí v jediný okamžík (od = do)
    const icalDate = ICAL.Time.fromJSDate(date);
    const occurrences = this.expander.expand(calendar, {
      startDate: icalDate,
      endDate: icalDate,
      maxIterations: this.maxIterations
    });
    // Vrátí true, pokud existuje alespoň jedna událost, nebo výskyt opakování.
    return Boolean(occurrences.length);
  }

  /**
   * Zjistí, jestli je v kalendáři nějaká aktivní událost pro datum na vstupu.
   * @param {string} ical Řetězec s iCalendar dle RFC 5545
   * @param {Date} from Počáteční datum pro hledání událostí
   * @param {Date} to Koncové datum pro hledání událostí
   * @returns {Date|null} čas aktuální události nebo čas začátku následující události.
   * Pokud nebyla nalezena žádná událost, pak se vrátí null.
   */
  currentOrNextValid(ical, from, to) {
    const calendar = ICAL.Component.fromString(ical);
    // Vyhledání událostí v jediný okamžík (od = do)
    const fromIcalDate = ICAL.Time.fromJSDate(from);
    const toIcalDate = ICAL.Time.fromJSDate(to);
    let occurrences = this.expander.expand(calendar, {
      startDate: fromIcalDate,
      endDate: toIcalDate,
      maxIterations: this.maxIterations
    });
    if (occurrences.length === 0) {
      return null;
    }

    occurrences = occurrences.sort(
      (event1, event2) =>
        event1.startDate.toUnixTime() - event2.startDate.toUnixTime()
    );

    // Pokud je více než jedna událost ve vyhledávaném intervalu, pak se vrátí čas startu první.
    return DateTime.fromSeconds(
      occurrences[0].startDate.toUnixTime()
    ).toJSDate();
  }
}

export default IcalPlanner;
